// src/AudioSpectrum/effects/wavyStaff.js
import { getHSLColor } from '../colorUtils.js';

// 음표 타입별 유니코드 심볼
const NOTE_SYMBOLS = {
  whole: '𝅝',
  half: '𝅗𝅥',
  quarter: '𝅘𝅥',
  eighth: '𝅘𝅥𝅮',
  sixteenth: '𝅘𝅥𝅯'
};

// 5선보의 웨이브 계산 (모든 곳에서 동일하게 사용)
const calculateStaffWave = (x, baseY, time, frequencyData, width) => {
  const freqIndex = Math.floor((x / width) * Math.min(frequencyData.length / 4, 60));
  const amplitude = (frequencyData[freqIndex] || 0) / 255 * 8;
  
  const wave1 = Math.sin((x * 0.008) + (time * 0.001)) * amplitude;
  const wave2 = Math.cos((x * 0.012) + (time * 0.0015)) * amplitude * 0.4;
  const wave3 = Math.sin((x * 0.005) + (time * 0.0008)) * amplitude * 0.2;
  
  return baseY + wave1 + wave2 + wave3;
};

// 음계 감지 - 더 많은 음표 생성
const detectNotes = (frequencyData) => {
  const notes = [];
  const A4 = 440;
  
  // 더 넓은 범위에서 더 많은 음표 검사
  for (let i = 3; i < Math.min(frequencyData.length / 3, 80); i += 3) {
    const amplitude = frequencyData[i];
    
    if (amplitude > 60) { // 임계값 낮춤
      const frequency = (i * 22050) / frequencyData.length;
      
      if (frequency > 65 && frequency < 2000) {
        const noteNumber = Math.round(12 * Math.log2(frequency / A4)) + 69;
        const octave = Math.floor(noteNumber / 12) - 1;
        const noteIndex = noteNumber % 12;
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        
        if (octave >= 2 && octave <= 7) {
          const staffPos = getStaffPosition(noteNames[noteIndex], octave);
          
          notes.push({
            note: noteNames[noteIndex],
            octave,
            frequency,
            amplitude,
            staffPosition: staffPos,
            color: getHSLColor((octave * 60 + noteIndex * 30) % 360, 75, 65),
            noteType: getNoteType(amplitude),
            intensity: amplitude / 255,
            freqBand: i
          });
        }
      }
    }
  }
  
  return notes.slice(0, 8); // 최대 8개로 증가
};

// 음표 타입 결정 (더 세분화)
const getNoteType = (amplitude) => {
  if (amplitude > 200) return 'whole';
  if (amplitude > 160) return 'half';
  if (amplitude > 120) return 'quarter';
  if (amplitude > 80) return 'eighth';
  return 'sixteenth';
};

// 5선보 위치 계산 (C4를 중앙선으로)
const getStaffPosition = (note, octave) => {
  const noteOrder = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };
  const baseName = note.replace('#', '');
  return noteOrder[baseName] + (octave - 4) * 7 - 4;
};

// 음표가 속한 5선보 라인 찾기
const getStaffLine = (staffPosition) => {
  const lines = [-4, -2, 0, 2, 4];
  let closestLine = 0;
  let minDistance = Math.abs(staffPosition - lines[0]);
  
  for (let i = 1; i < lines.length; i++) {
    const distance = Math.abs(staffPosition - lines[i]);
    if (distance < minDistance) {
      minDistance = distance;
      closestLine = i;
    }
  }
  
  return { lineIndex: closestLine, offset: staffPosition - lines[closestLine] };
};

// 연음 그룹 찾기
const findBeamGroups = (notes) => {
  const beamGroups = [];
  const beamableTypes = ['eighth', 'sixteenth'];
  
  for (let i = 0; i < notes.length; i++) {
    const note = notes[i];
    if (!beamableTypes.includes(note.noteType) || note.grouped) continue;
    
    const group = [note];
    note.grouped = true;
    
    // 인근 음표들과 그룹 만들기
    for (let j = i + 1; j < notes.length && group.length < 4; j++) {
      const nextNote = notes[j];
      if (beamableTypes.includes(nextNote.noteType) && 
          !nextNote.grouped &&
          Math.abs(nextNote.x - note.x) < 120 &&
          Math.abs(nextNote.freqBand - note.freqBand) < 15) {
        group.push(nextNote);
        nextNote.grouped = true;
      }
    }
    
    if (group.length >= 2) {
      beamGroups.push(group);
    }
  }
  
  return beamGroups;
};

// 연음 그리기
const drawBeams = (ctx, beamGroups, staffSpacing) => {
  beamGroups.forEach(group => {
    if (group.length < 2) return;
    
    const firstNote = group[0];
    const lastNote = group[group.length - 1];
    
    // 연음 색상 (그룹의 평균 색상)
    const avgHue = group.reduce((sum, note) => {
      const hue = parseInt(note.color.match(/hsl\((\d+)/)[1]);
      return sum + hue;
    }, 0) / group.length;
    
    const beamColor = getHSLColor(avgHue, 60, 55);
    ctx.strokeStyle = beamColor;
    ctx.fillStyle = beamColor;
    
    // 기본 연음 (8분음표용)
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    // 연음 높이 계산 (음표들 위쪽으로)
    const beamY1 = firstNote.y - 25;
    const beamY2 = lastNote.y - 25;
    
    // 연음 그리기 (사다리꼴 모양)
    ctx.beginPath();
    ctx.moveTo(firstNote.x, beamY1);
    ctx.lineTo(lastNote.x, beamY2);
    ctx.lineTo(lastNote.x, beamY2 + 4);
    ctx.lineTo(firstNote.x, beamY1 + 4);
    ctx.closePath();
    ctx.fill();
    
    // 16분음표가 있으면 이중 연음
    const hasSixteenth = group.some(note => note.noteType === 'sixteenth');
    if (hasSixteenth) {
      ctx.beginPath();
      ctx.moveTo(firstNote.x, beamY1 + 8);
      ctx.lineTo(lastNote.x, beamY2 + 8);
      ctx.lineTo(lastNote.x, beamY2 + 12);
      ctx.lineTo(firstNote.x, beamY1 + 12);
      ctx.closePath();
      ctx.fill();
    }
    
    // 음표에서 연음까지 기둥 그리기
    group.forEach(note => {
      if (note.intensity > 0.4) {
        ctx.strokeStyle = note.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(note.x + 8, note.y);
        ctx.lineTo(note.x + 8, beamY1 + (beamY2 - beamY1) * ((note.x - firstNote.x) / (lastNote.x - firstNote.x)));
        ctx.stroke();
      }
    });
  });
};

export const drawWavyStaff = (ctx, frequencyData, width, height) => {
  const time = Date.now();
  
  // 정적 데이터 초기화
  if (!drawWavyStaff.notes) {
    drawWavyStaff.notes = [];
    drawWavyStaff.beamGroups = [];
  }
  
  // 우아한 배경
  const bgGradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height)/2);
  bgGradient.addColorStop(0, '#1a1a3a');
  bgGradient.addColorStop(0.7, '#0f0f2a');
  bgGradient.addColorStop(1, '#050511');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, width, height);
  
  // 음악적 배경 파티클 (개수 증가)
  const avgFreq = frequencyData.reduce((a, b) => a + b, 0) / frequencyData.length;
  if (avgFreq > 25) {
    for (let i = 0; i < 12; i++) {
      const x = (Math.sin(time * 0.0002 + i * 1.2) * 0.4 + 0.5) * width;
      const y = (Math.cos(time * 0.0003 + i * 0.8) * 0.3 + 0.4) * height;
      const pulse = Math.sin(time * 0.008 + i) * 0.3 + 0.7;
      
      ctx.globalAlpha = pulse * 0.2;
      ctx.fillStyle = getHSLColor((time * 0.05 + i * 30) % 360, 60, 80);
      ctx.beginPath();
      ctx.arc(x, y, 1.5 + pulse * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
  
  // 5선보 그리기
  const staffSpacing = height / 20;
  const centerY = height / 2;
  const staffLines = 5;
  
  // 5선보 라인들과 각각의 웨이브 위치 저장
  const staffWavePositions = [];
  
  for (let line = 0; line < staffLines; line++) {
    const baseY = centerY + (line - 2) * staffSpacing;
    const lineIntensity = Math.min(avgFreq / 80, 1);
    
    const hue = (time * 0.03 + line * 45) % 360;
    ctx.strokeStyle = getHSLColor(hue, 30 + lineIntensity * 40, 60 + lineIntensity * 20);
    ctx.lineWidth = 0.5;
    
    ctx.beginPath();
    const wavePoints = [];
    
    for (let x = 0; x <= width; x += 3) {
      const waveY = calculateStaffWave(x, baseY, time, frequencyData, width);
      wavePoints.push({ x, y: waveY });
      
      if (x === 0) ctx.moveTo(x, waveY);
      else ctx.lineTo(x, waveY);
    }
    
    ctx.stroke();
    staffWavePositions[line] = wavePoints;
    
    if (lineIntensity > 0.4) {
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = 3;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }
  
  // 높은음자리표
  ctx.fillStyle = getHSLColor((time * 0.08) % 360, 50, 75);
  ctx.font = 'bold 50px serif';
  ctx.textAlign = 'left';
  ctx.fillText('𝄞', 20, centerY + 10);
  
  // 음표 생성 및 관리
  const detectedNotes = detectNotes(frequencyData);
  
  // 새 음표 추가 (개수 증가)
  detectedNotes.forEach(note => {
    const exists = drawWavyStaff.notes.some(n => 
      n.note === note.note && n.octave === note.octave && n.age < 800
    );
    
    if (!exists && drawWavyStaff.notes.length < 30) { // 최대 12개로 증가
      const staffInfo = getStaffLine(note.staffPosition);
      
      drawWavyStaff.notes.push({
        ...note,
        x: -30,
        age: 0,
        maxAge: 400 + Math.random() * 200,
        speed: 0.6 + note.intensity * 0.6 + Math.random() * 0.3,
        staffLineIndex: staffInfo.lineIndex,
        staffOffset: staffInfo.offset,
        life: 1.0,
        trail: [],
        sparkles: [],
        grouped: false
      });
    }
  });
  
  // 음표 애니메이션
  drawWavyStaff.notes = drawWavyStaff.notes.filter(note => {
    note.age++;
    note.x += note.speed;
    
    // 5선보 라인의 웨이브를 따라 Y 위치 계산
    if (staffWavePositions[note.staffLineIndex]) {
      const xIndex = Math.floor((note.x / width) * (staffWavePositions[note.staffLineIndex].length - 1));
      const clampedIndex = Math.max(0, Math.min(staffWavePositions[note.staffLineIndex].length - 1, xIndex));
      
      if (staffWavePositions[note.staffLineIndex][clampedIndex]) {
        note.y = staffWavePositions[note.staffLineIndex][clampedIndex].y + (note.staffOffset * staffSpacing / 4);
      }
    }
    
    // note.life = Math.max(0, 1 - (note.age / note.maxAge));
    note.life = 1;
    
    // 트레일 효과
    if (note.age % 4 === 0) {
      note.trail.push({ x: note.x, y: note.y, life: 0.6 });
    }
    note.trail = note.trail.filter(t => {
      t.life -= 0.03;
      return t.life > 0;
    }).slice(-8);
    
    // 강한 음표에만 반짝임
    if (note.intensity > 0.6 && Math.random() < 0.12) {
      note.sparkles.push({
        x: note.x + (Math.random() - 0.5) * 15,
        y: note.y + (Math.random() - 0.5) * 10,
        life: 1.0,
        size: Math.random() * 1.5 + 0.5,
        color: note.color
      });
    }
    
    note.sparkles = note.sparkles.filter(s => {
      s.life -= 0.025;
      return s.life > 0;
    });
    
    return note.life > 0 && note.x < width + 50;
  });
  
  // 화면에 있는 음표들만 골라서 연음 그룹 찾기
  const visibleNotes = drawWavyStaff.notes.filter(note => note.x > 0 && note.x < width);
  visibleNotes.forEach(note => note.grouped = false); // 그룹 초기화
  const beamGroups = findBeamGroups(visibleNotes);
  
  // 연음 그리기
  drawBeams(ctx, beamGroups, staffSpacing);
  
  // 음표 그리기
  drawWavyStaff.notes.forEach(note => {
    if (note.life > 0 && note.x < width + 50) {
      // 트레일
      note.trail.forEach((t, i) => {
        ctx.globalAlpha = t.life * (i / note.trail.length) * 0.5;
        ctx.fillStyle = note.color;
        ctx.beginPath();
        ctx.arc(t.x, t.y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      });
      
      // 반짝임
      note.sparkles.forEach(s => {
        ctx.globalAlpha = s.life * 0.8;
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fill();
        
        // 십자 반짝임
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(s.x - s.size * 2, s.y);
        ctx.lineTo(s.x + s.size * 2, s.y);
        ctx.moveTo(s.x, s.y - s.size * 2);
        ctx.lineTo(s.x, s.y + s.size * 2);
        ctx.stroke();
      });
      
      ctx.globalAlpha = note.life;
      
      // 음표 자체
      const size = 36 + note.intensity * 4;
      const pulseScale = 1 + Math.sin(note.age * 0.08) * 0.08 * note.intensity;
      
      ctx.save();
      ctx.translate(note.x, note.y);
      ctx.scale(pulseScale, pulseScale);
      
      if (note.intensity > 0.5) {
        ctx.shadowColor = note.color;
        ctx.shadowBlur = size * 0.2;
      }
      
      ctx.fillStyle = note.color;
      ctx.font = `${size}px serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
    //   // 연음 그룹에 속하지 않은 8분음표, 16분음표는 개별 기둥 그리기
    //   if ((note.noteType === 'eighth' || note.noteType === 'sixteenth') && 
    //       !note.grouped && note.intensity > 0.4) {
        
    //     // 기둥
    //     ctx.strokeStyle = note.color;
    //     ctx.lineWidth = 2;
    //     ctx.beginPath();
    //     ctx.moveTo(8, 0);
    //     ctx.lineTo(8, -20);
    //     ctx.stroke();
        
    //     // 깃발
    //     ctx.fillStyle = note.color;
    //     ctx.beginPath();
    //     ctx.moveTo(8, -20);
    //     ctx.bezierCurveTo(20, -18, 18, -12, 8, -14);
    //     ctx.fill();
        
    //     // 16분음표 추가 깃발
    //     if (note.noteType === 'sixteenth') {
    //       ctx.beginPath();
    //       ctx.moveTo(8, -16);
    //       ctx.bezierCurveTo(18, -14, 16, -8, 8, -10);
    //       ctx.fill();
    //     }
    //   }
      
      ctx.fillText(NOTE_SYMBOLS[note.noteType] || NOTE_SYMBOLS.quarter, 0, 0);
      ctx.restore();
      
      // 음계명 (선택적으로)
      if (note.intensity > 0.7 && note.x > 20 && note.x < width - 50) {
        ctx.font = 'bold 9px Arial';
        ctx.fillStyle = note.color;
        ctx.textAlign = 'center';
        ctx.fillText(`${note.note}${note.octave}`, note.x, note.y + size + 10);
      }
      
      ctx.globalAlpha = 1;
    }
  });
  
  // 강한 베이스에서 미묘한 전체 진동
  const bassAvg = frequencyData.slice(0, 15).reduce((a, b) => a + b, 0) / 15;
  if (bassAvg > 120) {
    const shake = (bassAvg / 255) * 0.8;
    ctx.save();
    ctx.translate(
      Math.sin(time * 0.02) * shake,
      Math.cos(time * 0.025) * shake * 0.5
    );
    ctx.restore();
  }
};